// @flow
import React, {useEffect, useState} from 'react';
import Modal, {SIZES} from 'common/componentsV2/modal/Modal';
import {Box} from '@material-ui/core';
import {TypographyBox} from 'common/componentsV2/boxTools';
import SearchBox from 'common/componentsV2/SearchBox';
import Button, {COLORS} from 'common/componentsV2/Button';
import EmptyListImageComponent from 'common/componentsV2/EmptyListImageComponent';
import UserEventsTable from 'userEvents/components/UserEventsTable/UserEventsTable';
import './UserEvents.module.scss';

type PropTypes = {
  events: Array,
  selectedEventId: string,
  timeZoneName: string,
  chart: React.Node,
  chartTitle: React.Node,
  isOpen: boolean,
  onClose: Function,
};

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const EmptyListIndication = (props: {isLoading: boolean, topEvents: Array}) => {
  if (props.isLoading || props.topEvents.length) {
    return null;
  }

  return (
    <div styleName="empty-list">
      <EmptyListImageComponent />
      <div styleName="empty-text">No Events Found - Refine Search</div>
    </div>
  );
};

const UserEventsChartModal = ({
  events,
  selectedEventId,
  timeZoneName,
  chart,
  chartTitle,
  isOpen,
  onClose,
}: PropTypes) => {
  const [event, setEvent] = useState(EMPTY_OBJECT);
  const [topEvents, setTopEvents] = useState(EMPTY_ARRAY);
  const [filter, setFilter] = useState('');

  // set the selected user event from which to take topEvents
  useEffect(() => {
    if (events && events.length) {
      const findEvent = events.find((ev) => ev.id === selectedEventId);
      setEvent(findEvent || EMPTY_OBJECT);
    } else {
      setEvent(EMPTY_OBJECT);
    }
  }, [events, selectedEventId]);

  // set the selected topEvents and filtered
  useEffect(() => {
    if (event && event.topEvents) {
      setTopEvents(event.topEvents.filter((ev) => ev.title.toLowerCase().indexOf(filter.toLowerCase()) !== -1));
    } else {
      setTopEvents(EMPTY_ARRAY);
    }
  }, [event, filter]);

  const onFilter = (val) => {
    setFilter(val);
  };

  return (
    <Modal isOpen={isOpen} size={SIZES.LARGE} onClose={onClose} isCloseButtonHidden isStatic>
      <Box mb={2} top="-6px" position="relative">
        <TypographyBox textAlign="center" lineHeight={1} fontWeight={400} fontSize={24}>
          Events
        </TypographyBox>
      </Box>
      {chartTitle && <div>{chartTitle}</div>}
      <Box height={250}>{chart}</Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <TypographyBox fontWeight={400} fontSize={24}>
          {topEvents.length !== event.totalEvents
            ? `${topEvents.length}/${event.totalEvents} Events`
            : `${event.totalEvents} Events`}
        </TypographyBox>
        <Box mt={1} width={240}>
          <SearchBox filter={filter} onFilter={onFilter} placeHolder="Search events" />
        </Box>
      </Box>
      <Box mt={1.5} height={250}>
        {topEvents.length ? (
          <UserEventsTable
            events={topEvents}
            isLoading={event === EMPTY_OBJECT}
            searchString={filter}
            timeZoneName={timeZoneName}
          />
        ) : null}
        <EmptyListIndication topEvents={topEvents} />
      </Box>
      <Box mt={2} display="flex" alignItems="center" justifyContent="flex-end">
        <Button colorSchema={COLORS.BLUE_500} text="Done" automationId="closeEventsModal" onClick={onClose} />
      </Box>
    </Modal>
  );
};

export default React.memo(UserEventsChartModal);
